export const SUBJECT_TO_CERTIFICATION = {
  CLOSING_OF_TRANSACTION: 'or such later date as the closing of the transaction occurs',
  EQUIPMENT_RESPONSIBILITY: 'or such later date as the Equipment becomes the responsibility of the Insured',
  DELIVERY_ACCEPTANCE_AIRCRAFT: 'subject to delivery and acceptance of the Aircraft by the Insured',
  DELIVERY_ACCEPTANCE_EQUIPMENT: 'subject to delivery and acceptance of the Equipment by the Insured',
  EFFECTIVENESS_OF_TRANSACTION: 'subject to effectiveness of the respective transaction',
  AIRCRAFT_DELIVERED_ACCEPTED: 'or such later date as the Aircraft is delivered and accepted by the Insured',
  NONE: 'none',
} as const
export type SubjectToCertification = typeof SUBJECT_TO_CERTIFICATION[keyof typeof SUBJECT_TO_CERTIFICATION]

export const AUTHORITIES_SPECIAL_FORMAT = {
  CANADA_INSURANCE: 'Canada Insurance',
  CANADA_REINSURANCE: 'Canada Reinsurance',
  GERMANY: 'Germany',
  ITALY: 'Italy',
  JORDAN: 'Jordan',
  US_DD_FORM_2400: 'US DD Form 2400',
  US_OST_FORM_6411: 'US OST Form 6411',
  ACCORD: 'ACCORD',
  NONE: 'none',
} as const
export type AuthoritiesSpecialFormat = typeof AUTHORITIES_SPECIAL_FORMAT[keyof typeof AUTHORITIES_SPECIAL_FORMAT]

export const CERTIFICATE_AUTHOR = {
  ALBATROS: 'Albatros',
  THIRD_PARTY: 'Third Party',
  ALLIANZ: 'Allianz',
} as const
export type CertificateAuthor = typeof CERTIFICATE_AUTHOR[keyof typeof CERTIFICATE_AUTHOR]

export const OBJECT_TYPE = {
  AIRCRAFT: 'Aircraft',
  ENGINE: 'Engine',
  EQUIPMENT: 'Equipment',
} as const
export type ObjectType = typeof OBJECT_TYPE[keyof typeof OBJECT_TYPE]

export const ENGINE_GROSSUP_CLAUSE = {
  AIRCRAFT_WITH_FOREIGN_ENGINES: 'Aircraft with foreign engines',
  ENGINES_ON_FOREIGN_AIRCRAFT: 'Engines on foreign aircraft',
  TITLED_ENGINES_ON_FOREIGN_AIRCRAFT: 'Titled Engines on foreign aircraft',
  NONE: 'none'
} as const
export type EngineGrossupClause = typeof ENGINE_GROSSUP_CLAUSE[keyof typeof ENGINE_GROSSUP_CLAUSE]

export const FLEET_LIST_FORMAT = {
  DEFAULT: 'default'
} as const
export type FleetListFormat = typeof FLEET_LIST_FORMAT[keyof typeof FLEET_LIST_FORMAT]

export const EU785_COVER_PACKAGE = {
  ALL_LIMITS: 'all limits',
  THIRD_PARTY_LIABS: 'third party liabs',
  LOAD_ONLY: 'load only',
  NONE: 'none',
} as const
export type Eu785CoverPackage = typeof EU785_COVER_PACKAGE[keyof typeof EU785_COVER_PACKAGE]
/** The SDR amount is always given in SDR; its currency select is locked to this value. */
export const SDR_CURRENCY = 'SDR'

export const EXCLUSION_LIABILITY = {
  WILFUL_MISCONDUCT: 'wilful misconduct',
  WM_AND_GROSS_NEGLIGENCE: 'w/m and gross negligence',
  WM_AND_NEGLIGENCE: 'w/m and negligence',
  WM_AND_RECKLESSNESS: 'w/m and recklessness',
  TO_THE_EXTENT_INSURANCE_INDEMNITY: 'to the extent insurance/ indemnity',
  NONE: 'none',
} as const
export type ExclusionLiability = typeof EXCLUSION_LIABILITY[keyof typeof EXCLUSION_LIABILITY]

export const CERTIFICATE_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  SUPERSEDED: 'superseded',
  CANCELLED: 'cancelled'
} as const
export type CertificateStatus = typeof CERTIFICATE_STATUS[keyof typeof CERTIFICATE_STATUS]

export const DOCUMENT_STATUS = {
  NONE: 'Status Null',
  GENERATED: 'generated',
  SENT: 'sent'
} as const
export type DocumentStatus = typeof DOCUMENT_STATUS[keyof typeof DOCUMENT_STATUS]

export const SUBJECT_TO_CERTIFICATION_OPTIONS: SubjectToCertification[] = Object.values(SUBJECT_TO_CERTIFICATION)
export const AUTHORITIES_SPECIAL_FORMAT_OPTIONS: AuthoritiesSpecialFormat[] = Object.values(AUTHORITIES_SPECIAL_FORMAT)
export const CERTIFICATE_AUTHOR_OPTIONS: CertificateAuthor[] = Object.values(CERTIFICATE_AUTHOR)
export const OBJECT_TYPE_OPTIONS: ObjectType[] = Object.values(OBJECT_TYPE)
export const ENGINE_GROSSUP_CLAUSE_OPTIONS: EngineGrossupClause[] = Object.values(ENGINE_GROSSUP_CLAUSE)
export const FLEET_LIST_FORMAT_OPTIONS: FleetListFormat[] = Object.values(FLEET_LIST_FORMAT)
export const EU785_COVER_PACKAGE_OPTIONS: Eu785CoverPackage[] = Object.values(EU785_COVER_PACKAGE)
export const EXCLUSION_LIABILITY_OPTIONS: ExclusionLiability[] = Object.values(EXCLUSION_LIABILITY)
export const CERTIFICATE_STATUS_OPTIONS: CertificateStatus[] = Object.values(CERTIFICATE_STATUS)
export const DOCUMENT_STATUS_OPTIONS: DocumentStatus[] = Object.values(DOCUMENT_STATUS)

/**
 * Resolves a raw backend value (either the enum key/code, e.g. `ALBATROS`, or the enum value/label,
 * e.g. `Albatros`) to the enum value/label so it matches the option lists used by the selects.
 * Falls back to the raw value if no match is found.
 */
export function enumValueFromRaw<T extends Record<string, string>>(
  enumObject: T,
  raw: string | null | undefined
): T[keyof T] | null {
  if (raw === null || raw === undefined || raw === '') return null
  const match = Object.entries(enumObject).find(([key, value]) => key === raw || value === raw)
  return (match?.[1] as T[keyof T]) ?? (raw as T[keyof T])
}

/**
 * Reverse of {@link enumValueFromRaw}: maps a form value (the enum value/label or key) back to the
 * enum key/code expected by the backend. Falls back to the raw value if no match is found.
 */
export function enumKeyFromRaw<T extends Record<string, string>>(
  enumObject: T,
  raw: string | null | undefined
): string | null {
  if (raw === null || raw === undefined || raw === '') return null
  const match = Object.entries(enumObject).find(([key, value]) => key === raw || value === raw)
  return match?.[0] ?? raw
}
